import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Delete, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// Email keyboard layout (English letters + @ and .)
const emailLayout = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["z", "x", "c", "v", "b", "n", "m"],
];

// Common email domains for quick selection
const emailDomains = [
  "@gmail.com",
  "@hotmail.com",
  "@outlook.com",
  "@yahoo.com",
  ".co.il",
  ".com",
];

export default function ContactsSettings() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, dir } = useTranslation();
  
  const [email, setEmail] = useState("");

  const saveCaregiverInfo = useCallback(async () => {
    if (!email.includes("@") || !email.includes(".")) {
      toast({
        title: (t as any)("invalidEmail") || "אימייל לא תקין",
        description: (t as any)("enterValidEmail") || "יש להזין כתובת אימייל תקינה",
        variant: "destructive",
      });
      return;
    }
    
    const settings = getUserSettings();
    await saveUserSettings({ ...settings, caregiverEmail: email });
    toast({
      title: t("savedSuccessfully"),
      description: t("caregiverUpdated"),
    });
    navigate("/contacts");
  }, [email, navigate, t, toast]);

  useEffect(() => {
    const settings = getUserSettings();
    setEmail(settings.caregiverEmail || "");
  }, []);

  const handleKeyPress = (key: string) => {
    setEmail(prev => prev + key);
  };

  const handleBackspace = () => {
    setEmail(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setEmail("");
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background p-4 overflow-auto" dir={dir}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-4"
        >
          <h1 className="text-2xl font-bold text-foreground">{t("caregiverSettings")}</h1>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex-1 flex flex-col max-w-3xl mx-auto w-full"
        >
          <div className="bg-card rounded-2xl border-2 border-border p-4 space-y-4 flex-1 flex flex-col">
            {/* Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-lg font-semibold">{t("caregiverEmail")}</h2>
            </div>

            {/* Email Display & Action Bar */}
            <div className="flex items-center gap-2 p-2 rounded-xl transition-all">
              <div
                className="flex-1 min-h-[56px] px-4 py-3 rounded-xl bg-muted border-2 border-border flex items-center overflow-x-auto"
                dir="ltr"
              >
                {email ? (
                  <span className="text-lg font-medium font-poppins">{email}</span>
                ) : (
                  <span className="text-muted-foreground">email@example.com</span>
                )}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setEmail(prev => prev + "@")}
                data-scan-item
                className="w-12 h-12 shrink-0 text-lg font-bold"
              >
                @
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleBackspace}
                disabled={!email}
                data-scan-item
                className="w-12 h-12 shrink-0"
              >
                <Delete className="w-5 h-5" />
              </Button>
              <Button
                onClick={saveCaregiverInfo}
                disabled={!email.includes("@")}
                data-scan-item
                className="h-12 px-6 gap-2"
              >
                <Check className="w-5 h-5" />
                {t("save")}
              </Button>
            </div>

            {/* Domain Shortcuts */}
            <div className="flex flex-wrap gap-2 justify-center p-2 rounded-xl transition-all">
              {emailDomains.map((domain, index) => (
                <motion.button
                  key={domain}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setEmail(prev => prev + domain)}
                  data-scan-item
                  className="px-4 py-2 rounded-xl bg-accent/20 hover:bg-accent/40 border-2 border-accent/30 text-accent font-medium transition-all font-poppins text-sm"
                >
                  {domain}
                </motion.button>
              ))}
            </div>

            {/* Email Keyboard */}
            <div className="flex-1 flex flex-col gap-1.5 justify-center">
              {emailLayout.map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="flex gap-1.5 justify-center"
                    dir="ltr"
                  >
                    {row.map((key, keyIndex) => (
                      <motion.button
                        key={key}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleKeyPress(key)}
                        data-scan-item
                        className={cn(
                          "w-12 h-12 rounded-xl",
                          "bg-muted hover:bg-primary/20 border-2 border-border hover:border-primary/50 transition-all",
                          "flex items-center justify-center font-bold text-foreground text-lg font-poppins"
                        )}
                      >
                        {key}
                      </motion.button>
                    ))}
                  </div>
              ))}

              {/* Action Row */}
              <div className="flex gap-2 justify-center mt-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClear}
                  data-scan-item
                  className="px-6 h-12 rounded-xl bg-destructive/20 hover:bg-destructive/30 border-2 border-destructive/30 transition-all flex items-center justify-center gap-2 text-destructive font-medium"
                >
                  {t("clear") || "נקה"}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setEmail(prev => prev + ".")}
                  data-scan-item
                  className="w-16 h-12 rounded-xl bg-muted hover:bg-muted/80 border-2 border-border transition-all flex items-center justify-center font-bold text-2xl font-poppins"
                >
                  .
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setEmail(prev => prev + "-")}
                  data-scan-item
                  className="w-16 h-12 rounded-xl bg-muted hover:bg-muted/80 border-2 border-border transition-all flex items-center justify-center font-bold text-2xl font-poppins"
                >
                  -
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
}
